import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AvatarModule } from 'primeng/avatar';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { PageToolbarComponent } from '../../components/page-toolbar/page-toolbar.component';
import { ConferenceService } from '../../services/conference.service';
import { ConferenceLogDto } from '../../dto/conference.dto';
import { QueryResult, QueryResultItem } from '../../components/common-dto/query.dto';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-conference-log-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    TableModule,
    ProgressSpinnerModule,
    AvatarModule,
    CardModule,
    ButtonModule,
    PageToolbarComponent,
  ],
  template: `
    <pb-page-toolbar
      [header]="'Conference Logs'"
      [supportsAdd]="false"
      [supportsEdit]="false"
      [isEditing]="false"
    ></pb-page-toolbar>

    <div class="logs-container">
      <div class="search-container">
        <span class="p-input-icon-right full-width">
          <i class="pi pi-search"></i>
          <input
            type="text"
            pInputText
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchInput()"
            placeholder="Search logs by title, note, or user..."
            class="auto-width"
          />
        </span>
      </div>

      <div class="logs-grid" *ngIf="!loading && logs.length > 0">
        <p-card *ngFor="let log of logs" class="log-card">
          <div class="log-card-content">
            <div class="log-header">
              <div class="log-user">
                <p-avatar
                  [image]="log.user.avatar"
                  [label]="log.user.initials || getInitials(log.user.name)"
                  shape="circle"
                  size="normal"
                ></p-avatar>
                <div class="user-info">
                  <div class="user-name">{{ log.user.name }}</div>
                  <div class="log-date">{{ formatDateTime(log.dateTime) }}</div>
                </div>
              </div>
            </div>
            <h4 class="log-title">{{ log.title }}</h4>
            <p class="log-note">{{ log.note }}</p>
            <div *ngIf="log.imageUrl" class="log-image">
              <img [src]="log.imageUrl" [alt]="log.title" />
            </div>
          </div>
        </p-card>
      </div>

      <div *ngIf="loading" class="loading-container">
        <p-progressSpinner></p-progressSpinner>
        <p>Loading logs...</p>
      </div>

      <div *ngIf="!loading && logs.length === 0" class="empty-state">
        <i class="pi pi-file empty-icon"></i>
        <h3>No logs found</h3>
        <p>There are currently no conference logs available.</p>
      </div>

      <!-- Pagination -->
      <div *ngIf="!loading && logs.length > 0" class="pagination-container">
        <button
          pButton
          type="button"
          label="Previous"
          icon="pi pi-chevron-left"
          [disabled]="currentPage === 0"
          (click)="previousPage()"
          [outlined]="true"
        ></button>
        <span class="page-info">
          Page {{ currentPage + 1 }} of {{ totalPages }}
        </span>
        <button
          pButton
          type="button"
          label="Next"
          icon="pi pi-chevron-right"
          iconPos="right"
          [disabled]="currentPage >= totalPages - 1"
          (click)="nextPage()"
          [outlined]="true"
        ></button>
      </div>
    </div>
  `,
  styles: [
    `
      .logs-container {
        padding: 1rem;
      }

      .search-container {
        margin-bottom: 1.5rem;
      }

      .full-width {
        width: 100%;
        display: block;
      }

      .auto-width {
        width: 100%;
      }

      .logs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 1.5rem;
        margin-bottom: 2rem;
      }

      .log-card {
        height: 100%;
      }

      .log-card-content {
        padding: 0;
      }

      .log-header {
        margin-bottom: 1rem;
      }

      .log-user {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .user-info {
        display: flex;
        flex-direction: column;
      }

      .user-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: var(--text-color);
        margin-bottom: 0.25rem;
      }

      .log-date {
        font-size: 0.85rem;
        color: var(--text-color-secondary);
      }

      .log-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0 0 0.75rem 0;
        color: var(--text-color);
      }

      .log-note {
        font-size: 1rem;
        line-height: 1.6;
        color: var(--text-color);
        margin: 0 0 1rem 0;
        white-space: pre-wrap;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .log-image {
        margin-top: 1rem;
        border-radius: 8px;
        overflow: hidden;
      }

      .log-image img {
        width: 100%;
        height: auto;
        display: block;
        max-height: 300px;
        object-fit: cover;
      }

      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        gap: 1rem;
      }

      .empty-state {
        text-align: center;
        padding: 4rem 2rem;
      }

      .empty-icon {
        font-size: 4rem;
        color: var(--text-color-secondary);
        margin-bottom: 1rem;
      }

      .empty-state h3 {
        font-size: 1.5rem;
        margin: 0 0 0.5rem 0;
      }

      .empty-state p {
        color: var(--text-color-secondary);
      }

      .pagination-container {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        padding: 2rem 0;
      }

      .page-info {
        font-size: 1rem;
        color: var(--text-color);
      }

      :host ::ng-deep .p-input-icon-right {
        width: 100%;
      }

      :host ::ng-deep .p-input-icon-right input {
        width: 100%;
      }

      :host ::ng-deep .p-avatar {
        width: 40px;
        height: 40px;
      }

      @media screen and (max-width: 768px) {
        .logs-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class ConferenceLogListComponent implements OnInit {
  logs: ConferenceLogDto[] = [];
  searchQuery: string = '';
  loading = true;
  totalRecords = 0;
  conferenceId: string = '';
  currentPage = 0;
  pageSize = 10;
  totalPages = 0;

  private searchSubject = new Subject<string>();

  constructor(
    private conferenceService: ConferenceService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.conferenceId = this.route.snapshot.params['conferenceId'] || '';

    // Setup debounced search
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 0;
        this.loadData();
      });

    this.loadData();
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  loadData(): void {
    this.loading = true;
    const skip = this.currentPage * this.pageSize;
    const take = this.pageSize;

    const queryParams: any = {
      skip,
      take,
    };

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      queryParams.filter = this.searchQuery.trim();
    }

    this.conferenceService
      .getConferenceLogs(this.conferenceId, queryParams)
      .subscribe({
        next: (result: QueryResult<ConferenceLogDto>) => {
          this.logs = result.items.map((item) => item.item);
          this.totalRecords = result.total;
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading logs:', error);
          this.loading = false;
        },
      });
  }

  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadData();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadData();
    }
  }

  formatDateTime(date: Date): string {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}

